import { useEffect } from "react";
import cc from "classcat";
import { useNavigate, useParams } from "react-router-dom";
import { useNotificationContext } from "cerulean";
import {
  Alert,
  Row,
  ContentCard,
  Button,
  ResponsiveFlex,
} from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import { entities, modules, cents } from "byzantine";
import filters from "byzantine/src/filters";
import { useIsACHDualApprovalRequired } from "byzantine/src/dbbl/businessLogic/entities/users/hooks";
import { useShowUserNeedsApprovalAlert } from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";
import { PAYMENT_ACTIVITY_ROUTE } from "../../PaymentActivity/routes";
import { ACH_PAYMENT_ROUTE } from "./ACHPaymentRoutes";
import { formatFrequencyValue } from "../FrequencySelector";
import styles from "./ReceiptScreen.module.scss";

const ReceiptScreen = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const features = useUserFeatures();
  const { sendNotification } = useNotificationContext();
  const { uuid } = useParams();
  const form = modules.achPayments.ACHPaymentForm.useForm();
  const isDebitPayment = modules.achPayments.useIsDebitACHPayment();
  const selectedRecipient = entities.recipients.useSelectedRecipient();
  const achDualApprovalRequired = useIsACHDualApprovalRequired();
  const { showAlert } = useShowUserNeedsApprovalAlert("ach");
  const receiptSubtitle = modules.achPayments.useACHReceiptSubtitle();
  const selectedDeliverySpeedDetails =
    modules.achPayments.useSelectedDeliverySpeedDetails();
  const { payment_activity: paymentActivityEnabled } = features;
  const { resetForm } = form;
  const rejectedPayment: API.FormattedPaymentActivity | null = null;

  if (!selectedRecipient) return null;

  const {
    values: { amount, deliverySpeed, recurringRule, startDate },
  } = form;
  const { name } = selectedRecipient;

  const handleClickDone = () => {
    if (paymentActivityEnabled) {
      navigate(`/${PAYMENT_ACTIVITY_ROUTE}/${uuid}`);
    } else {
      navigate("/");
    }
  };

  const handleClickCTA = () => {
    resetForm();
    navigate(`/transfer/${ACH_PAYMENT_ROUTE}`);
  };

  useEffect(() => {
    if (showAlert) {
      sendNotification({
        type: "negative",
        text: l10n.getString("ach-payment-wire-approval-org-requires-users"),
      });
    }
  }, [showAlert]);

  const displayFrequency = recurringRule?.frequency && recurringRule?.isVisible;
  const frequencyValue = displayFrequency
    ? formatFrequencyValue(recurringRule.frequency, l10n)
    : l10n.getString("table-cell-frequency-one-time");
  const isRejectedFedNowPayment = Boolean(rejectedPayment);
  const isExpeditedPayment =
    deliverySpeed === "instant" || deliverySpeed === "sameDay";
  let deliverySpeedLabel = `${l10n.getString("label-standard")} ACH`;
  if (deliverySpeed === "sameDay") {
    deliverySpeedLabel = l10n.getString("label-same-day");
  } else if (deliverySpeed === "instant") {
    deliverySpeedLabel = l10n.getString("label-instant");
  }
  let title = l10n.getString("ach-payment-receipt-title");
  if (isRejectedFedNowPayment) {
    title = l10n.getString("ach-payment-rejected-title");
  } else if (achDualApprovalRequired) {
    title = l10n.getString("ach-payment-receipt-dual-approval-title");
  } else if (isExpeditedPayment) {
    title = l10n.getString("invoice-payment-receipt-title");
  }

  let recipientLabel = l10n.getString("single-ach-payment-recipient-send-to");
  if (!isRejectedFedNowPayment && isDebitPayment) {
    recipientLabel = l10n.getString(
      "single-ach-payment-recipient-collect-from"
    );
  }

  const speedLabel = isRejectedFedNowPayment
    ? l10n.getString("speed-label")
    : l10n.getString("label-delivery-speed");
  const rejectedSpeedValue = l10n.getString("label-transfer-rejected");

  return (
    <div className={styles.receiptContainer}>
      <ContentCard>
        <div className={styles.cardContainer}>
          <h2 className="fontColor--heading">
            {title}
            <i
              className={`narmi-icon-${
                isRejectedFedNowPayment ? "x" : "check"
              } margin--left--xs`}
            />
          </h2>
          {!isRejectedFedNowPayment ? (
            <p
              className={`fontColor--secondary fontSize--s ${styles.receiptSubtitle}`}
            >
              {receiptSubtitle}
            </p>
          ) : null}
          {isRejectedFedNowPayment ? (
            <Alert isActive isDismissable={false} kind="error">
              {l10n.getString("ach-payment-rejected-alert")}
            </Alert>
          ) : null}
          <div
            className={cc([
              styles.contentContainer,
              {
                [styles.rejectedContentContainer]: isRejectedFedNowPayment,
              },
            ])}
          >
            <ResponsiveFlex gapSize="l">
              <Row>
                <Row.Item>
                  <p>{recipientLabel}</p>
                </Row.Item>
                <Row.Item shrink>
                  <p>{name}</p>
                </Row.Item>
              </Row>
              <Row>
                <Row.Item>
                  <p>{l10n.getString("label-amount")}</p>
                </Row.Item>
                <Row.Item shrink>
                  <p>{`${filters.currency(cents(amount))}`}</p>
                </Row.Item>
              </Row>
              {!isRejectedFedNowPayment ? (
                <Row>
                  <Row.Item>
                    <p>{l10n.getString("label-frequency")}</p>
                  </Row.Item>
                  <Row.Item shrink>
                    <p>{frequencyValue}</p>
                  </Row.Item>
                </Row>
              ) : null}
              <Row>
                <Row.Item>
                  <p>{speedLabel}</p>
                </Row.Item>
                <Row.Item shrink>
                  <p>
                    {isRejectedFedNowPayment
                      ? rejectedSpeedValue
                      : deliverySpeedLabel}
                  </p>
                </Row.Item>
              </Row>
              {!isRejectedFedNowPayment ? (
                <>
                  <Row>
                    <Row.Item>
                      <p>{l10n.getString("label-scheduled-date")}</p>
                    </Row.Item>
                    <Row.Item shrink>
                      <p>{startDate}</p>
                    </Row.Item>
                  </Row>
                  <Row>
                    <Row.Item>
                      <p>{l10n.getString("label-fee")}</p>
                    </Row.Item>
                    <Row.Item shrink>
                      <p>{selectedDeliverySpeedDetails.feeLabel}</p>
                    </Row.Item>
                  </Row>
                </>
              ) : null}
              {isRejectedFedNowPayment ? (
                <Row>
                  <Row.Item>
                    <p>{l10n.getString("label-payment-id")}</p>
                  </Row.Item>
                  <Row.Item shrink>
                    {/* TODO: populate payment ID when available */}
                    <p>-</p>
                  </Row.Item>
                </Row>
              ) : null}
            </ResponsiveFlex>
          </div>
          <div className={styles.buttonContainer}>
            <Button
              kind="tertiary"
              label={l10n.getString("ach-payment-receipt-cta")}
              testId="ach-payment-receipt-cta-button"
              onClick={handleClickCTA}
            />
            <Button
              label={l10n.getString("label-done")}
              testId="ach-payment-receipt-done"
              onClick={handleClickDone}
            />
          </div>
        </div>
      </ContentCard>
    </div>
  );
};

export default ReceiptScreen;
